/**
 * Support Tickets API — wraps the `/support_tickets` router on the eastview-os
 * backend (loan_processing.support_tickets, SupportTicket model).
 *
 * Ticket shape (returned by get / create / update):
 *   { _id, subject, description(html), category, severity, status, createdBy,
 *     createdDateTime, updatedDateTime, assignedTo, resolutionNotes(html),
 *     closeOutDate, resolvedBy, relatedDealId }
 *
 * Endpoints (all Bearer-authed via losHttp):
 *   GET  /support_tickets/meta            → { categories, severities, statuses }
 *   GET  /support_tickets/my?status=      → caller's created+assigned tickets
 *                                           (admins get ALL tickets)
 *   GET  /support_tickets/{id}            → one ticket (creator/assignee/admin)
 *   POST /support_tickets                 → create { subject, description,
 *                                           category, severity?, relatedDealId? }
 *   POST /support_tickets/{id}/update     → patch any of { assignedTo, status,
 *                                           severity, resolutionNotes,
 *                                           closeOutDate, category, subject,
 *                                           description }
 *
 * Backend side effects we rely on (don't replicate client-side):
 *   - create stamps createdBy + status=Open and posts a Teams notice.
 *   - status → Resolved/Closed auto-stamps closeOutDate + resolvedBy; reopening
 *     clears them. The frontend only sends `status`.
 */
import { losHttp } from "../lib/losHttp";

// Helpdesk categories only — feature requests are a separate object (excluded
// here on purpose). Used as a fallback when /meta is unavailable (e.g. before
// the router deploys); the live /meta response wins when present.
export const SUPPORT_TICKET_CATEGORIES = [
  "Create User",
  "Issue Report",
  "Data Issue",
];

export const SUPPORT_TICKET_SEVERITIES = ["Low", "Medium", "High", "Critical"];

export const SUPPORT_TICKET_STATUSES = [
  "Open",
  "In Progress",
  "Resolved",
  "Closed",
];

export const DEFAULT_SEVERITY = "Medium";

// Statuses that the backend treats as "closed out" (auto-stamps closeOutDate +
// resolvedBy). Surfaced in the UI to show/hide resolution fields.
export const RESOLVED_STATUSES = ["Resolved", "Closed"];

// MUI chip colors per severity / status (palette keys, not raw hex).
export const SEVERITY_COLOR = {
  Low: "default",
  Medium: "info",
  High: "warning",
  Critical: "error",
};

export const STATUS_COLOR = {
  Open: "info",
  "In Progress": "warning",
  Resolved: "success",
  Closed: "default",
};

export const supportTicketsApi = {
  // GET /support_tickets/meta → { categories, severities, statuses }
  getMeta: () => losHttp.get("/support_tickets/meta").then((r) => r.data),

  // GET /support_tickets/my?status= → created-by or assigned-to the caller;
  // admins get ALL tickets. `status` optional.
  listMine: (status) =>
    losHttp
      .get("/support_tickets/my", {
        params: status ? { status } : {},
      })
      .then((r) => r.data),

  // GET /support_tickets/{id} → one ticket (creator / assignee / admin only).
  get: (id) => losHttp.get(`/support_tickets/${id}`).then((r) => r.data),

  // POST /support_tickets → create. Returns the created ticket.
  create: ({ subject, description, category, severity, relatedDealId }) =>
    losHttp
      .post("/support_tickets", {
        subject,
        description,
        category,
        ...(severity ? { severity } : {}),
        ...(relatedDealId ? { relatedDealId } : {}),
      })
      .then((r) => r.data),

  // POST /support_tickets/{id}/update → patch. Returns the updated ticket.
  update: (id, fields) =>
    losHttp
      .post(`/support_tickets/${id}/update`, fields)
      .then((r) => r.data),

  // POST /support_tickets/{id}/comments { body: html } → add a note. Same
  // visibility as viewing the ticket (creator / assignee / admin); 400 on an
  // empty body. Returns the full updated ticket. The backend also posts the note
  // into the ticket's Slack thread (support-ticket-notes-and-slack.md §2).
  addComment: (id, body) =>
    losHttp
      .post(`/support_tickets/${id}/comments`, { body })
      .then((r) => r.data),
};

export default supportTicketsApi;
